import axios, {
  AxiosDefaults, AxiosInstance, AxiosInterceptorManager, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig,
} from 'axios';
import { AxiosObservable } from './axiosObservable.interface';
import { createObservable } from './createObservable';

export class Axios {
  static defaults: AxiosDefaults = axios.defaults;

  static interceptors: {
    request: AxiosInterceptorManager<InternalAxiosRequestConfig>;
    response: AxiosInterceptorManager<AxiosResponse>;
  } = axios.interceptors;

  constructor(private axiosInstance: AxiosInstance) {
  }

  get defaults() {
    return this.axiosInstance.defaults;
  }

  get interceptors() {
    return this.axiosInstance.interceptors;
  }

  static request<T = unknown>(config: AxiosRequestConfig): AxiosObservable<T> {
    return createObservable(axios.request, config);
  }

  static get<T = unknown>(url: string, config?: AxiosRequestConfig): AxiosObservable<T> {
    return createObservable<T>(axios.get, url, config);
  }

  static post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): AxiosObservable<T> {
    return createObservable<T>(axios.post, url, data, config);
  }

  static put<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): AxiosObservable<T> {
    return createObservable<T>(axios.put, url, data, config);
  }

  static patch<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): AxiosObservable<T> {
    return createObservable<T>(axios.patch, url, data, config);
  }

  static delete<T = unknown>(url: string, config?: AxiosRequestConfig): AxiosObservable<T> {
    return createObservable<T>(axios.delete, url, config);
  }

  static head<T = unknown>(url: string, config?: AxiosRequestConfig): AxiosObservable<T> {
    return createObservable<T>(axios.head, url, config);
  }

  static create(config: AxiosRequestConfig): Axios {
    return new Axios(axios.create(config));
  }

  requestInternal(config: AxiosRequestConfig): Promise<AxiosResponse> {
    return this.axiosInstance.request(config);
  }

  request<T = unknown>(config: AxiosRequestConfig): AxiosObservable<T> {
    return createObservable(this.axiosInstance.request, config);
  }

  get<T = unknown>(url: string, config?: AxiosRequestConfig): AxiosObservable<T> {
    return createObservable<T>(this.axiosInstance.get, url, config);
  }

  head<T = unknown>(url: string, config?: AxiosRequestConfig): AxiosObservable<T> {
    return createObservable<T>(this.axiosInstance.head, url, config);
  }

  post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): AxiosObservable<T> {
    return createObservable<T>(this.axiosInstance.post, url, data, config);
  }

  put<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): AxiosObservable<T> {
    return createObservable<T>(this.axiosInstance.put, url, data, config);
  }

  patch<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): AxiosObservable<T> {
    return createObservable<T>(this.axiosInstance.patch, url, data, config);
  }

  delete<T = unknown>(url: string, config?: AxiosRequestConfig): AxiosObservable<T> {
    return createObservable<T>(this.axiosInstance.delete, url, config);
  }
}
